import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { StyledAppBarContainer, StyledMenuIcon } from "./NavBar.styles";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Tooltip from "@mui/material/Tooltip";
import { signOut } from "firebase/auth";
import { auth } from "../../services/firebase";
import { useAuth } from "../../auth/AuthContext";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import LanguageSelector from "../../shared/ui/LanguageSelector/LanguageSelector";
import { useTranslation } from "react-i18next";

const drawerWidth = 240;

function DrawerAppBar(props) {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { window: containerWindow } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const container =
    containerWindow !== undefined ? () => containerWindow().document.body : undefined;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Shared active link style
  const navLinkSx = (path) => ({
    color: "#fff",
    borderRadius: "6px",
    px: 1.5,
    position: "relative",
    fontWeight: location.pathname === path ? 700 : 500,
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 4,
      left: "50%",
      transform: location.pathname === path ? "translateX(-50%) scaleX(1)" : "translateX(-50%) scaleX(0)",
      width: "60%",
      height: "2px",
      backgroundColor: "#fff",
      borderRadius: "2px",
      transition: "transform 0.2s ease",
    },
    "&:hover::after": {
      transform: "translateX(-50%) scaleX(1)",
    },
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
  });

  const themeToggle = (
    <Tooltip title={props.darkMode ? "Light Mode" : "Dark Mode"}>
      <IconButton
        onClick={props.toggleDarkMode}
        color="inherit"
        aria-label="toggle dark mode"
        sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
      >
        {props.darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
    </Tooltip>
  );

  // Logout only ever renders when already authenticated — no public
  // "Admin"/login entry point is exposed in the client-facing nav.
  const logoutButton = currentUser ? (
    <Tooltip title="Logout">
      <IconButton
        onClick={handleLogout}
        color="inherit"
        aria-label="logout"
        sx={{ "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" } }}
      >
        <LogoutIcon />
      </IconButton>
    </Tooltip>
  ) : null;

  // Desktop nav
  const tabs = (
    <>
      <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.5 }}>
        <Link to="/">
          <Button sx={navLinkSx("/")}>{t("nav.home")}</Button>
        </Link>
        <Link to="/about">
          <Button sx={navLinkSx("/about")}>{t("nav.about")}</Button>
        </Link>
        <Link to="/services">
          <Button sx={navLinkSx("/services")}>{t("nav.services")}</Button>
        </Link>
        <Link to="/contact">
          <Button sx={navLinkSx("/contact")}>{t("nav.contact")}</Button>
        </Link>
        <LanguageSelector />
        {themeToggle}
        {logoutButton}
      </Box>
    </>
  );

  // Mobile drawer
  const drawer = (
    <>
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: theme.palette.primary.dark,
            color: "#fff",
          },
        }}
      >
        <Box onClick={handleDrawerToggle} sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ my: 2 }}>
            <Link to="/">
              <Button sx={{ color: "#fff", fontWeight: 700 }} translate="no">
                IT Engine Sparks
              </Button>
            </Link>
          </Typography>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.15)" }} />
          <List>
            {[
              { label: t("nav.home"), path: "/" },
              { label: t("nav.about"), path: "/about" },
              { label: t("nav.services"), path: "/services" },
              { label: t("nav.contact"), path: "/contact" },
            ].map(({ label, path }) => (
              <ListItem key={path} component={Link} to={path} disablePadding>
                <ListItemButton
                  sx={{
                    textAlign: "center",
                    backgroundColor:
                      location.pathname === path
                        ? "rgba(255,255,255,0.12)"
                        : "transparent",
                  }}
                >
                  <ListItemText
                    sx={{
                      color: "#fff",
                      "& .MuiTypography-root": {
                        fontWeight: location.pathname === path ? 700 : 400,
                      },
                    }}
                  >
                    {label}
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem sx={{ justifyContent: "center", gap: 1 }}>
              <LanguageSelector />
              {themeToggle}
              {logoutButton}
            </ListItem>
          </List>
        </Box>
      </Drawer>
      <IconButton
        color="inherit"
        aria-label="open drawer"
        edge="start"
        onClick={handleDrawerToggle}
        sx={{ display: { sm: "block", md: "block" } }}
      >
        <StyledMenuIcon />
      </IconButton>
    </>
  );

  return (
    <Box sx={{ display: "flex", pt: "64px" }}>
      <CssBaseline />
      <StyledAppBarContainer>
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, display: { xs: "block", sm: "block" } }}
          >
            <Link to="/">
              <Button sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }} translate="no">
                IT Engine Sparks
              </Button>
            </Link>
          </Typography>
          {matches ? drawer : tabs}
        </Toolbar>
      </StyledAppBarContainer>
    </Box>
  );
}

DrawerAppBar.propTypes = {
  window: PropTypes.func,
};

export default DrawerAppBar;
